import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import { Save, Sparkles, Eye, Send, Share2, ExternalLink, ArrowLeft, RefreshCw, GripVertical, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { useProject } from '../contexts/ProjectContext';

function SortableArticleItem({ article, index, isFinal, showIntroSection, onEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: article._id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const art = article.output || article;
  const canEdit = !isFinal && !showIntroSection;

  return (
    <div ref={setNodeRef} style={style} className={cn("bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden", isDragging && "shadow-lg z-50")}>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 lg:px-6 py-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {canEdit && (
            <button {...attributes} {...listeners} className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0" title="Przeciągnij aby zmienić kolejność">
              <GripVertical className="w-5 h-5" />
            </button>
          )}
          <h3 className="text-lg font-semibold text-gray-900 flex-shrink-0">Artykuł {index + 1}</h3>
          <a href={art.link_zrodlowy} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 break-all line-clamp-1 min-w-0 flex-1">
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
            Link źródłowy
          </a>
          {canEdit && (
            <button onClick={() => onRemove(article._id)} className="flex-shrink-0 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Usuń artykuł">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="p-3 sm:p-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Tytuł:</label>
          <div
            contentEditable={canEdit}
            suppressContentEditableWarning
            onBlur={(e) => canEdit && onEdit(article._id, 'tytul', e.target.innerText)}
            className={cn(
              "w-full min-h-[60px] p-4 rounded-lg border-2 transition-colors break-all overflow-x-auto max-w-full",
              canEdit ? "border-dashed border-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                      : "border-gray-200 bg-gray-50 cursor-not-allowed text-gray-600"
            )}
          >
            {art.tytul}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Treść:</label>
          <div
            contentEditable={canEdit}
            suppressContentEditableWarning
            onBlur={(e) => canEdit && onEdit(article._id, 'tresc', e.target.innerText)}
            className={cn(
              "w-full min-h-[200px] p-4 rounded-lg border-2 transition-colors whitespace-pre-wrap break-all overflow-x-auto max-w-full",
              canEdit ? "border-dashed border-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                      : "border-gray-200 bg-gray-50 cursor-not-allowed text-gray-600"
            )}
          >
            {art.tresc}
          </div>
        </div>
      </div>
    </div>
  );
}

const API_BASE = '/api';

export function Preview() {
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const htmlPollIntervalRef = useRef(null);
  const introPollIntervalRef = useRef(null);
  const freshSaveRef = useRef(false); // true = właśnie zapisano, pomiń natychmiastowy check

  const [loading, setLoading] = useState(true);
  const [draftData, setDraftData] = useState(null);
  const [isFinal, setIsFinal] = useState(false);
  const [articles, setArticles] = useState([]);
  const [htmlKey, setHtmlKey] = useState(null);

  // Intro state
  const [showIntroSection, setShowIntroSection] = useState(false);
  const [titleOptions, setTitleOptions] = useState([]);
  const [introOptions, setIntroOptions] = useState([]);
  const [selectedTitleIndex, setSelectedTitleIndex] = useState(0);
  const [selectedIntroIndex, setSelectedIntroIndex] = useState(0);
  const [customTitle, setCustomTitle] = useState('');
  const [useCustomTitle, setUseCustomTitle] = useState(false);
  const [leadContent, setLeadContent] = useState('');
  const [finalTitle, setFinalTitle] = useState('');

  // Action states
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isGeneratingHTML, setIsGeneratingHTML] = useState(false);
  const [htmlReady, setHtmlReady] = useState(false);
  const [ghostUrl, setGhostUrl] = useState(null);

  useEffect(() => {
    if (!currentProject) return;
    fetchDraftData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  // When draft is final and we have htmlKey, check HTML status
  useEffect(() => {
    if (isFinal && htmlKey) {
      const startPolling = () => {
        setIsGeneratingHTML(true);
        htmlPollIntervalRef.current = setInterval(async () => {
          try {
            const pollResponse = await fetch(`${API_BASE}/actions.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ action: 'check-status', payload: { key: htmlKey } })
            });
            const pollData = await pollResponse.json();

            if (pollData.ready) {
              clearInterval(htmlPollIntervalRef.current);
              setIsGeneratingHTML(false);
              setHtmlReady(true);
              toast.success('Newsletter gotowy! HTML wygenerowany.');
            }
          } catch (error) {
            console.error('HTML polling error:', error);
          }
        }, 15000);
      };

      if (freshSaveRef.current) {
        // Świeży zapis — stary plik HTML może nadal istnieć w R2.
        // Nie sprawdzaj natychmiast, daj n8n czas na nadpisanie pliku.
        freshSaveRef.current = false;
        startPolling();
      } else {
        // Przeładowanie strony z istniejącym finalnym draftem — sprawdź od razu.
        const checkHTMLStatus = async () => {
          try {
            const statusResponse = await fetch(`${API_BASE}/actions.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ action: 'check-status', payload: { key: htmlKey } })
            });
            const statusData = await statusResponse.json();

            if (statusData.ready) {
              setHtmlReady(true);
              setIsGeneratingHTML(false);
            } else {
              startPolling();
            }
          } catch (error) {
            console.error('Error checking HTML status:', error);
            startPolling();
          }
        };
        checkHTMLStatus();
      }
    }

    return () => {
      if (htmlPollIntervalRef.current) {
        clearInterval(htmlPollIntervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinal, htmlKey]);

  const fetchDraftData = async () => {
    const projectId = currentProject;

    // Try to load from localStorage cache first
    const cacheKey = `draft_${projectId}`;
    const cached = localStorage.getItem(cacheKey);

    try {
      // Always fetch from API to get latest
      const response = await fetch(`${API_BASE}/draft.php`, { credentials: 'include' });
      if (response.status === 401) {
        navigate('/');
        return;
      }

      if (response.status === 404) {
        // No draft on server - try localStorage cache
        if (cached) {
          try {
            const cachedData = JSON.parse(cached);
            loadDraftFromData(cachedData, projectId);
            toast.success('Draft zaladowany z cache');
            return;
          } catch (e) {
            // ignore parse error
          }
        }
        toast.error('Nie znaleziono draftu');
        setLoading(false);
        return;
      }

      const result = await response.json();
      if (result.success) {
        const data = result.data;
        // Save to localStorage cache
        localStorage.setItem(cacheKey, JSON.stringify(data));
        loadDraftFromData(data, projectId);
      } else {
        toast.error('Nie znaleziono draftu');
        setLoading(false);
      }
    } catch (error) {
      // On network error, try localStorage cache
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          loadDraftFromData(cachedData, projectId);
          toast.success('Draft zaladowany z cache (offline)');
          return;
        } catch (e) {
          // ignore
        }
      }
      toast.error('Blad pobierania draftu');
      setLoading(false);
    }
  };

  const loadDraftFromData = (data, projectId) => {
    const isFinalDraft = !!data.final_title || !!data.is_final;
    setIsFinal(isFinalDraft);

    // Restore stored htmlKey if present
    if (data.draft_key) {
      const computedHtmlKey = data.draft_key.replace('newsletter_draft.json', 'preview.html');
      setHtmlKey(computedHtmlKey);
    }

    const withIds = (arr) => arr.map((a, i) => ({
      ...a,
      _id: a._id || a.output?.link_zrodlowy || a.link_zrodlowy || `art-${i}`
    }));

    if (isFinalDraft) {
      setArticles(withIds(data.articles || []));
      setFinalTitle(data.final_title || '');
      setLeadContent(data.final_lead || '');
      setShowIntroSection(true);
    } else {
      const articlesData = withIds(data.articles || data[0]?.data || []);

      // Restore title/intro from localStorage if available (scoped by draft_key)
      const currentDraftKey = data.draft_key || '';
      const savedTitle = localStorage.getItem(`draft_title_${projectId}_${currentDraftKey}`);
      const savedIntro = localStorage.getItem(`draft_intro_${projectId}_${currentDraftKey}`);

      // Clear stale title/intro from old format (without draft_key)
      localStorage.removeItem(`draft_title_${projectId}`);
      localStorage.removeItem(`draft_intro_${projectId}`);

      if (savedTitle || savedIntro) {
        try {
          if (savedTitle) {
            const titleData = JSON.parse(savedTitle);
            setTitleOptions(titleData.options || []);
            setSelectedTitleIndex(0);
          }
          if (savedIntro) {
            const introData = JSON.parse(savedIntro);
            setIntroOptions(introData.options || []);
            setLeadContent(introData.lead || '');
            setSelectedIntroIndex(0);
            setShowIntroSection(true);
          }
        } catch (e) {
          console.error('Error restoring title/intro from localStorage:', e);
        }
      }

      // Check for cached article edits
      const editsCacheKey = `draft_edits_${projectId}`;
      const cachedEdits = localStorage.getItem(editsCacheKey);

      if (cachedEdits) {
        try {
          const edits = JSON.parse(cachedEdits);
          const mergedArticles = articlesData.map((article, index) => {
            if (edits.articles && edits.articles[index]) {
              return {
                ...article,
                output: {
                  ...(article.output || {}),
                  ...edits.articles[index]
                }
              };
            }
            return article;
          });
          setArticles(mergedArticles);
          toast.success('Draft zaladowany (z zapisanymi edycjami)');
        } catch (e) {
          setArticles(articlesData);
          toast.success('Draft zaladowany');
        }
      } else {
        setArticles(articlesData);
        toast.success('Draft zaladowany');
      }
    }

    setDraftData(data);
    setLoading(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const saveArticleEdits = (updatedArticles) => {
    const editsCacheKey = `draft_edits_${currentProject}`;
    localStorage.setItem(editsCacheKey, JSON.stringify({
      articles: updatedArticles.map(a => ({
        tytul: a.output?.tytul || a.tytul,
        tresc: a.output?.tresc || a.tresc,
        link_zrodlowy: a.output?.link_zrodlowy || a.link_zrodlowy
      })),
      timestamp: new Date().toISOString()
    }));
  };

  const handleArticleEdit = (_id, field, value) => {
    setArticles(prev => {
      const updated = prev.map(a => {
        if (a._id !== _id) return a;
        return { ...a, output: { ...(a.output || {}), [field]: value } };
      });
      saveArticleEdits(updated);
      return updated;
    });
  };

  const handleRemoveArticle = (_id) => {
    setArticles(prev => {
      const updated = prev.filter(a => a._id !== _id);
      saveArticleEdits(updated);
      return updated;
    });
    toast.success('Artykuł usunięty');
  };

  const handleArticlesDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setArticles(prev => {
        const oldIndex = prev.findIndex(a => a._id === active.id);
        const newIndex = prev.findIndex(a => a._id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const articlesData = articles.map(article => ({
        output: {
          tytul: article.output?.tytul || article.tytul,
          tresc: article.output?.tresc || article.tresc,
          link_zrodlowy: article.output?.link_zrodlowy || article.link_zrodlowy
        }
      }));

      const response = await fetch(`${API_BASE}/draft.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'save',
          articles: articlesData
        })
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Edycja zapisana!');
        // Clear article edits from localStorage after saving to backend
        localStorage.removeItem(`draft_edits_${currentProject}`);
        // Update draft cache
        localStorage.removeItem(`draft_${currentProject}`);
      } else {
        toast.error('Blad podczas zapisywania');
      }
    } catch (error) {
      toast.error('Blad: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateIntro = async () => {
    setIsGenerating(true);
    try {
      const articlesForAI = articles.map(article => {
        const art = article.output || article;
        return {
          tytul: art.tytul,
          tresc: art.tresc
        };
      });

      // Still calls n8n via preview.php for AI generation
      const response = await fetch(`${API_BASE}/preview.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'generate-intro',
          key: draftData?.draft_key || 'local',
          content: { articles: articlesForAI }
        })
      });

      const result = await response.json();

      if (result.success && result.data) {
        const raw = Array.isArray(result.data) ? result.data[0] : result.data;
        const sessionData = raw?.output?.session || raw?.session || raw;

        if (sessionData && sessionData.current_titles && sessionData.current_intros) {
          const titles = sessionData.current_titles || [];
          const intros = sessionData.current_intros || [];
          const firstLead = intros[0]?.intro || '';

          setTitleOptions(titles);
          setIntroOptions(intros);
          setSelectedTitleIndex(0);
          setSelectedIntroIndex(0);
          setLeadContent(firstLead);
          setShowIntroSection(true);

          // Save to localStorage for refresh persistence (scoped by draft_key)
          const dk = draftData?.draft_key || '';
          localStorage.setItem(`draft_title_${currentProject}_${dk}`, JSON.stringify({ options: titles }));
          localStorage.setItem(`draft_intro_${currentProject}_${dk}`, JSON.stringify({ options: intros, lead: firstLead }));

          toast.success('Wstep wygenerowany!');
        } else {
          toast.error('Brak danych sesji w odpowiedzi');
        }
      } else {
        toast.error(result.error || 'Blad podczas generowania intro');
      }
    } catch (error) {
      toast.error('Blad: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveFinal = async () => {
    const selectedTitle = useCustomTitle
      ? customTitle
      : titleOptions[selectedTitleIndex]?.title;

    if (!selectedTitle) {
      toast.error('Wybierz tytul newslettera!');
      return;
    }

    setIsSaving(true);
    try {
      const articlesData = articles.map(article => ({
        output: {
          tytul: article.output?.tytul || article.tytul,
          tresc: article.output?.tresc || article.tresc,
          link_zrodlowy: article.output?.link_zrodlowy || article.link_zrodlowy
        }
      }));

      const response = await fetch(`${API_BASE}/draft.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'save-final',
          title: selectedTitle,
          lead: leadContent,
          articles: articlesData
        })
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Wersja finalna zapisana!');

        // Store html_key for HTML polling
        if (result.html_key) {
          freshSaveRef.current = true; // pomiń natychmiastowy check — stary plik może istnieć
          setHtmlKey(result.html_key);
          setIsGeneratingHTML(true);
        }

        setIsFinal(true);
        setFinalTitle(selectedTitle);

        // Clear draft cache so fresh data is loaded next time
        localStorage.removeItem(`draft_${currentProject}`);
        localStorage.removeItem(`draft_edits_${currentProject}`);
        const dk = draftData?.draft_key || '';
        localStorage.removeItem(`draft_title_${currentProject}_${dk}`);
        localStorage.removeItem(`draft_intro_${currentProject}_${dk}`);
      } else {
        toast.error('Blad podczas zapisywania');
      }
    } catch (error) {
      toast.error('Blad: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (htmlPollIntervalRef.current) {
        clearInterval(htmlPollIntervalRef.current);
      }
      if (introPollIntervalRef.current) {
        clearInterval(introPollIntervalRef.current);
      }
    };
  }, []);

  const handlePreviewHTML = () => {
    const key = htmlKey || 'enriched-articles/preview.html';
    const s3Url = `https://pub-a03e21fe32554db195ca0ec9a85b069b.r2.dev/${key}`;
    window.open(s3Url, '_blank');
  };

  const clearAllLocalStorage = () => {
    if (!currentProject) return;
    const today = new Date().toISOString().split('T')[0];
    localStorage.removeItem(`top5_${currentProject}`);
    localStorage.removeItem(`top5_${currentProject}_${today}`);
    localStorage.removeItem(`draft_${currentProject}`);
    localStorage.removeItem(`draft_edits_${currentProject}`);
    // Clear title/intro cache (both old and new format)
    localStorage.removeItem(`draft_title_${currentProject}`);
    localStorage.removeItem(`draft_intro_${currentProject}`);
    const dk = draftData?.draft_key || '';
    if (dk) {
      localStorage.removeItem(`draft_title_${currentProject}_${dk}`);
      localStorage.removeItem(`draft_intro_${currentProject}_${dk}`);
    }
    localStorage.setItem('newsletter_published', 'true');
  };

  const handlePublishToGhost = async () => {
    setIsPublishing(true);
    try {
      console.log('Publishing to Ghost with html_key:', htmlKey);

      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'publish-ghost',
          payload: { html_key: htmlKey }
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Opublikowano w Ghost!');
        if (result.ghost_url) {
          setGhostUrl(result.ghost_url);
        }

        // Clear draft file on server
        fetch(`${API_BASE}/draft.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ action: 'clear' })
        }).catch(e => console.error('Draft clear error:', e));

        // Clear all localStorage
        clearAllLocalStorage();

        // Trigger LinkedIn Generation
        fetch(`${API_BASE}/actions.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ action: 'generate-linkedin', payload: {} })
        }).catch(e => console.error('LinkedIn trigger error:', e));

        toast.info('Generowanie postow LinkedIn...', { duration: 5000 });

        const today = new Date().toISOString().split('T')[0];
        localStorage.setItem('waiting_for_reels', JSON.stringify({
          date: today,
          started_at: new Date().toISOString()
        }));

        toast.info('Generowanie scenariuszy rolek... (3-5 min)', { duration: 5000 });
      } else {
        toast.error('Blad podczas publikacji: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Blad: ' + error.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleGenerateSocial = async () => {
    setIsPublishing(true);
    try {
      const payload = {
        ghost_post_id: finalTitle || 'test',
        newsletter_title: finalTitle,
        newsletter_lead: leadContent
      };

      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'generate-social',
          payload: payload
        })
      });

      const result = await response.json();

      if (result.status === 'processing') {
        toast.success('Generowanie posta i grafiki... (1-2 min)');

        const socialKey = htmlKey ? htmlKey.replace('preview.html', 'social_post.json') : null;

        if (socialKey) {
          const socialPollInterval = setInterval(async () => {
            try {
              const statusResponse = await fetch(`https://n8n.studiogo.pl/webhook/check-draft-status?key=${encodeURIComponent(socialKey)}`);
              const statusData = await statusResponse.json();

              if (statusData.ready) {
                clearInterval(socialPollInterval);
                toast.success('Post social gotowy! Przekierowuje...');
                navigate(`/social-edit?key=${encodeURIComponent(socialKey)}`);
              }
            } catch (error) {
              console.error('Social polling error:', error);
            }
          }, 10000);
        }
      } else {
        toast.error('Blad podczas generowania');
        setIsPublishing(false);
      }
    } catch (error) {
      toast.error('Blad: ' + error.message);
      setIsPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-xl font-semibold text-muted-foreground">Ladowanie draftu...</div>
        </div>
      </div>
    );
  }

  if (!draftData) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-8 text-center">
          <h2 className="text-xl font-bold text-destructive mb-2">Nie znaleziono draftu</h2>
          <p className="text-muted-foreground">Wróc do Dashboard i zaakceptuj newsletter, aby wygenerowac draft.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-4 lg:p-6">
      {/* Header */}
      <header className="mb-6 sticky top-0 bg-background z-10 pb-4 border-b">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <button onClick={() => navigate('/')} className="text-sm text-primary hover:underline mb-2 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Powrót do Dashboard
            </button>
            <h2 className="text-2xl lg:text-3xl font-bold">Edycja Newslettera</h2>
            <p className="text-muted-foreground mt-1">
              Status: <span className={cn("font-medium", isFinal ? "text-green-600" : "text-orange-600")}>
                {isFinal ? 'Finalna wersja' : 'Draft'}
              </span>
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            {!isFinal && !showIntroSection && (
              <>
                <button onClick={handleSaveDraft} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm sm:text-base">
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Zapisywanie...' : 'Zapisz Edycje'}
                </button>
                <button onClick={handleGenerateIntro} disabled={isGenerating} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm sm:text-base">
                  <Sparkles className="w-4 h-4" />
                  {isGenerating ? 'Generowanie...' : 'Wygeneruj Wstep'}
                </button>
              </>
            )}

            {isGenerating && (
              <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-md font-medium text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Czekam na wstep i tytuly...
              </div>
            )}

            {showIntroSection && !isFinal && (
              <button onClick={handleSaveFinal} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 text-sm sm:text-base">
                <Save className="w-4 h-4" />
                {isSaving ? 'Zapisywanie...' : 'Zapisz Finalne'}
              </button>
            )}

            {isFinal && isGeneratingHTML && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-md font-medium text-sm">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generowanie HTML...
                </div>
                <button
                  onClick={() => { setHtmlReady(true); setIsGeneratingHTML(false); if (!htmlKey) setHtmlKey('enriched-articles/preview.html'); if (htmlPollIntervalRef.current) clearInterval(htmlPollIntervalRef.current); }}
                  className="px-3 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-md text-xs transition-colors"
                  title="HTML jest gotowy - pomiń oczekiwanie"
                >
                  HTML gotowy ✓
                </button>
              </div>
            )}

            {isFinal && !htmlReady && !isGeneratingHTML && (
              <button
                onClick={() => { setHtmlReady(true); if (!htmlKey) setHtmlKey('enriched-articles/preview.html'); }}
                className="px-3 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-md text-xs transition-colors"
                title="Przejdź do publikacji jeśli HTML jest już gotowy"
              >
                HTML gotowy ✓
              </button>
            )}

            {isFinal && htmlReady && (
              <>
                <button onClick={handlePreviewHTML} className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors text-sm sm:text-base">
                  <Eye className="w-4 h-4" />
                  HTML
                </button>
                {!ghostUrl ? (
                  <button onClick={handlePublishToGhost} disabled={isPublishing} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 text-sm sm:text-base">
                    <Send className="w-4 h-4" />
                    {isPublishing ? '...' : 'Ghost'}
                  </button>
                ) : (
                  <a href={ghostUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm sm:text-base">
                    <ExternalLink className="w-4 h-4" />
                    Ghost
                  </a>
                )}
                <button onClick={handleGenerateSocial} disabled={isPublishing} className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors disabled:opacity-50 text-sm sm:text-base">
                  <Share2 className="w-4 h-4" />
                  {isPublishing ? '...' : 'Social'}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Intro Section */}
      {showIntroSection && (
        <section className="mb-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-sm border border-purple-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 px-4 lg:px-6 py-4 border-b border-purple-200">
            <h2 className="text-lg lg:text-xl font-semibold text-gray-900">Wstep i Tytul</h2>
          </div>

          <div className="p-3 sm:p-6 space-y-6">
            {/* Title Selection */}
            {isFinal ? (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                <p className="text-sm font-medium text-green-800 mb-1">Finalny Tytul:</p>
                <p className="text-lg font-bold text-gray-900 break-all">{finalTitle}</p>
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-semibold mb-3">Wybierz Tytul Newslettera:</h3>
                <div className="space-y-2">
                  {titleOptions.map((titleObj, index) => (
                    <label key={index} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-white/50 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="newsletter_title"
                        checked={!useCustomTitle && selectedTitleIndex === index}
                        onChange={() => {
                          setUseCustomTitle(false);
                          setSelectedTitleIndex(index);
                        }}
                        className="mt-1 flex-shrink-0"
                      />
                      <span className="flex-1 font-medium break-all min-w-0">{titleObj.title}</span>
                    </label>
                  ))}

                  <label className="flex items-start gap-3 p-3 border rounded-lg hover:bg-white/50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="newsletter_title"
                      checked={useCustomTitle}
                      onChange={() => setUseCustomTitle(true)}
                      className="mt-1 flex-shrink-0"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="font-medium block mb-2">Własny tytul:</span>
                      <input
                        type="text"
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value)}
                        onFocus={() => setUseCustomTitle(true)}
                        placeholder="Wpisz wlasny tytul"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Lead Content */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Wstep (Lead):</label>
              {isFinal ? (
                <div className="min-h-[120px] p-4 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-600 break-all">
                  {leadContent}
                </div>
              ) : (
                <div className="w-full overflow-hidden">
                  <div
                    contentEditable={!isFinal}
                    suppressContentEditableWarning
                    onBlur={(e) => setLeadContent(e.target.innerText)}
                    className="w-full min-h-[120px] p-4 rounded-lg border-2 border-dashed border-purple-300 hover:border-purple-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-colors break-all overflow-x-auto max-w-full"
                  >
                    {leadContent}
                  </div>
                </div>
              )}
            </div>

            {/* Intro Variants */}
            {!isFinal && introOptions.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Wybierz Wariant Wstepu:</h3>
                <div className="space-y-3">
                  {introOptions.map((introObj, index) => (
                    <label key={index} className="flex items-start gap-3 p-4 border rounded-lg hover:bg-white/50 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="newsletter_intro"
                        checked={selectedIntroIndex === index}
                        onChange={() => {
                          setSelectedIntroIndex(index);
                          setLeadContent(introObj.intro);
                        }}
                        className="mt-1 flex-shrink-0"
                      />
                      <div className="flex-1 text-sm bg-white p-3 rounded border break-all min-w-0">
                        {introObj.intro}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Articles Section */}
      <section className="space-y-4">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 rounded-lg flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">📄 Artykuły ({articles.length})</h2>
          {!isFinal && !showIntroSection && (
            <span className="text-xs text-gray-400">Przeciągnij aby zmienić kolejność · X aby usunąć</span>
          )}
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleArticlesDragEnd}>
          <SortableContext items={articles.map(a => a._id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {articles.map((article, index) => (
                <SortableArticleItem
                  key={article._id}
                  article={article}
                  index={index}
                  isFinal={isFinal}
                  showIntroSection={showIntroSection}
                  onEdit={handleArticleEdit}
                  onRemove={handleRemoveArticle}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </section>
    </div>
  );
}
